import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { configuracionSistemaNormalize } from '../../../Objetos/ConfiguracionesSistemaModel';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ConfiguracionesSistemaModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import '../../../Styles/ColoresCatalog.css'; // Reutilizamos estilos

const PAGE_SIZE = 12;
const endpoint = ENDPOINTS.configuracionesSistema;
const endpointById = ENDPOINTS.configuracionSistemaById;
const modelo = configuracionSistemaNormalize;

export default function ConfiguracionesSistemaCatalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);
        } catch (error) {
            console.error('Error al cargar configuraciones:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((e) => {
            return normalizeValue(e.nombre).toLowerCase().includes(q) ||
                normalizeValue(e.descripcion).toLowerCase().includes(q) ||
                normalizeValue(e.valor).toLowerCase().includes(q);
        });
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelectedId(item.idConfiguracion);
        setEditData(item);
        setModalMode('edit');
        setModalOpen(true);
    };

    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            const payload = {
                IdConfiguracion: mode === 'new' ? 0 : selectedId,
                Nombre: form.nombre,
                Descripcion: form.descripcion,
                Valor: form.valor,
                Activo: form.activo
            };

            if (mode === 'new') {
                await axios.post(endpoint, payload);
            } else {
                await axios.put(endpoint, payload);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">Configuraciones de Sistema</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar configuración..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Nombre (Clave)</th>
                                    <th>Descripción</th>
                                    <th>Valor</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idConfiguracion}>
                                        <td style={{ fontWeight: 600 }}>{r.nombre}</td>
                                        <td>{r.descripcion}</td>
                                        <td style={{
                                            fontFamily: '"Fira Code", monospace',
                                            fontSize: '0.85rem',
                                            maxWidth: '300px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }} title={r.nombre === 'CORREO_NOTIFICACIONES_CLAVE' ? 'Contraseña Oculta' : r.valor}>
                                            {r.nombre === 'CORREO_NOTIFICACIONES_CLAVE' ? '••••••••••••' : r.valor}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--edit"
                                                onClick={() => handleEdit(r)}
                                                aria-label="Editar"
                                                title="Editar"
                                            >
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--del"
                                                onClick={() => askDelete(r.idConfiguracion, r.nombre)}
                                                aria-label="Borrar"
                                                title="Borrar"
                                            >
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>Sin resultados encontrados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                    </div>
                    <div className="roles-pagination-right">
                        <button className={`roles-page-btn ${page === 1 ? 'is-disabled' : ''}`} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                            <button key={n} className={`roles-page-btn ${n === page ? 'is-active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        <button className={`roles-page-btn ${page === totalPages ? 'is-disabled' : ''}`} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div className="roles-editor-slot" ref={editorRef}>
                    <Modal
                        open={modalOpen}
                        mode={modalMode}
                        data={editData}
                        onClose={() => setModalOpen(false)}
                        onSubmit={handleSubmit}
                        inline={true}
                        triggerAlert={triggerAlert}
                    />
                </div>
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
